import { useGlobalConfig } from 'hooks/useAppRoot/useGlobalConfig';
import { useLocalization } from 'hooks/useAppRoot/useLocalization';
import {
  FEATURE_TOGGLE_KEYS,
  useFeatureToggle,
} from 'hooks/useAppRoot/useFeatureToggle';
import { useMemo } from 'preact/hooks';
import { GlobalConfig } from 'interfaces/GlobalConfig.interface';
import { FeatureToggles } from 'interfaces/FeatureToggle.interface';

interface UseAppRootProps {
  cmsSlug: string;
}

interface AppRootData {
  globalConfig: GlobalConfig;
  locale: string;
  featureToggles: FeatureToggles;
}

interface UseAppRootReturn {
  data: AppRootData;
  error: unknown;
  isSuccess: boolean;
}

export const useAppRoot = ({ cmsSlug }: UseAppRootProps): UseAppRootReturn => {
  const {
    data: globalConfig,
    error: globalConfigError,
    isLoading: isLoadingGlobalConfig,
  } = useGlobalConfig({ cmsSlug });

  const { locale, isInitializingLocale } = useLocalization({
    language: globalConfig?.config?.language,
  });

  const {
    data: featureToggles,
    error: featureToggleError,
    isLoading: isLoadingFeatureToggle,
  } = useFeatureToggle(globalConfig?.organizationSlug, [
    FEATURE_TOGGLE_KEYS.PTP_EVENT_VISIBILITY,
    FEATURE_TOGGLE_KEYS.RETURN_MANAGEMENT,
    FEATURE_TOGGLE_KEYS.CAMPAIGN_MANAGER,
  ]);

  const isSuccess = useMemo(() => {
    if (
      !Object.keys(globalConfig || {}).length ||
      !Object.keys(featureToggles || {}).length ||
      isLoadingGlobalConfig ||
      isLoadingFeatureToggle ||
      !isInitializingLocale
    ) {
      return false;
    }

    return true;
  }, [
    globalConfig,
    featureToggles,
    isLoadingGlobalConfig,
    isLoadingFeatureToggle,
    isInitializingLocale,
  ]);

  return {
    data: {
      globalConfig,
      locale,
      featureToggles,
    },
    error: globalConfigError || featureToggleError,
    isSuccess,
  };
};
